import calculateAvailability from "./calculateAvailability";
import { dayNumberInData, sumProduct } from "../functions";
import { mean, sum } from "mathjs";
import type {
  CalcOptions,
  DataIndex,
  DaySettingsData,
  NumberMatrix,
  TechnologyDatum,
} from "../algorithm.types";

type CalculateLongTermStorageParams = {
  calcOptions: CalcOptions;
  daySettingsData: DaySettingsData;
  dataIndex: Pick<DataIndex, "longTermStorage">;
  technologyData: ReadonlyArray<
    Pick<TechnologyDatum, "efficiency" | "zeroCarbon">
  >;
  genPowerResults: Array<number>;
  demandCalcData: NumberMatrix;
  sliderValues: Array<number>;
};

type CalculateLongTermStorageResult = {
  genPowerResults: Array<number>;
  demandCalcData: NumberMatrix;
};

export default function calculateLongTermStorage({
  calcOptions,
  daySettingsData,
  dataIndex,
  technologyData,
  genPowerResults,
  demandCalcData,
  sliderValues,
}: CalculateLongTermStorageParams): CalculateLongTermStorageResult {
  const {
    dayYearPercent,
    noOfStorTypes,
    noOfTimeSlots,
    noOfTechs,
    noOfDaysPerSeason,
    noOfSeasons,
  } = calcOptions;
  let { dayOfTheSeason, season } = calcOptions;

  // VARIABLES FOR PUMP STORAGE CALCULATIONS
  let expectedZeroCarbonGenDaily = Array(noOfDaysPerSeason + 1).fill(0);

  const residualDemandDaily = Array(noOfDaysPerSeason).fill(0);
  let avgResidualDemandDaily;

  let maxStoredEnergy;
  let maxDeliveredEnergy;
  const pumpPower = Array(noOfDaysPerSeason + 1).fill(0);
  const pumpEfficiency = technologyData[dataIndex.longTermStorage].efficiency;
  const pumpCap = sliderValues[dataIndex.longTermStorage];
  const genPower = Array(noOfDaysPerSeason).fill(0);

  let deliveryFactor;
  let currentSeasonDayYearPercentage = [0];

  for (season = 1; season <= noOfSeasons; season++) {
    currentSeasonDayYearPercentage = [0]; // resets this array
    expectedZeroCarbonGenDaily = Array(noOfDaysPerSeason + 1).fill(0); // For each Season:
    for (
      dayOfTheSeason = 1;
      dayOfTheSeason <= noOfDaysPerSeason;
      dayOfTheSeason++
    ) {
      calcOptions.season = season;
      calcOptions.dayOfTheSeason = dayOfTheSeason;

      const availData = calculateAvailability({
        sliderValues,
        calcOptions,
        daySettingsData,
      });

      currentSeasonDayYearPercentage.push(
        dayYearPercent[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ],
      );

      for (let i = 0; i < noOfTechs - noOfStorTypes; i++) {
        if (technologyData[i].zeroCarbon === 1) {
          expectedZeroCarbonGenDaily[dayOfTheSeason] += mean(availData[i]);
        }
      }

      residualDemandDaily[dayOfTheSeason] =
        mean(
          demandCalcData[
            dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
          ],
        ) - expectedZeroCarbonGenDaily[dayOfTheSeason];
    } // End day loop

    avgResidualDemandDaily =
      sumProduct(residualDemandDaily, currentSeasonDayYearPercentage) /
      sum(currentSeasonDayYearPercentage);

    maxStoredEnergy = 0;
    maxDeliveredEnergy = 0;

    for (
      dayOfTheSeason = 1;
      dayOfTheSeason <= noOfDaysPerSeason;
      dayOfTheSeason++
    ) {
      pumpPower[dayOfTheSeason] = Math.min(
        pumpCap,
        avgResidualDemandDaily - residualDemandDaily[dayOfTheSeason],
      );
      pumpPower[dayOfTheSeason] = Math.max(pumpPower[dayOfTheSeason], 0);
      maxStoredEnergy +=
        pumpPower[dayOfTheSeason] *
        8760 *
        dayYearPercent[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ] *
        pumpEfficiency;

      genPower[dayOfTheSeason] = Math.min(
        pumpCap,
        residualDemandDaily[dayOfTheSeason] - avgResidualDemandDaily,
      );
      genPower[dayOfTheSeason] = Math.max(genPower[dayOfTheSeason], 0);
      maxDeliveredEnergy +=
        genPower[dayOfTheSeason] *
        8760 *
        dayYearPercent[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ];
    } // End day loop

    deliveryFactor = maxStoredEnergy / maxDeliveredEnergy;

    if (deliveryFactor < 1) {
      for (
        dayOfTheSeason = 1;
        dayOfTheSeason <= noOfDaysPerSeason;
        dayOfTheSeason++
      ) {
        genPower[dayOfTheSeason] *= deliveryFactor;
      }
    } else {
      for (
        dayOfTheSeason = 1;
        dayOfTheSeason <= noOfDaysPerSeason;
        dayOfTheSeason++
      ) {
        pumpPower[dayOfTheSeason] /= deliveryFactor;
      }
    }

    for (
      dayOfTheSeason = 1;
      dayOfTheSeason <= noOfDaysPerSeason;
      dayOfTheSeason++
    ) {
      for (let j = 0; j < noOfTimeSlots; j++) {
        demandCalcData[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ][j] += pumpPower[dayOfTheSeason];
        demandCalcData[
          dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
        ][j] -= genPower[dayOfTheSeason];
      }
      genPowerResults[
        dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
      ] = genPower[dayOfTheSeason];
    }
  } // Next season

  return { genPowerResults, demandCalcData };
}
